/**
 * AdminOrganizationsTab — list, create, edit and delete organizations via the admin API.
 */

import { type FormEvent, useCallback, useEffect, useRef, useState } from "react";
import {
  authFetch,
  authFetchJson,
  getRetentionImpact,
  updateOrganizationRetention,
  type RetentionImpact,
} from "@/lib/api";
import { ROLE_LEVEL, useAuth } from "@/lib/hooks/useAuth";
import { normalizeSlug } from "@/lib/slug";
import { Pagination } from "@/components/Pagination";
import { formatAudioRetention } from "./audioRetentionLabel";

interface Organization {
  id: string;
  name: string;
  slug: string;
  admin_emails?: string[];
  settings?: Record<string, unknown>;
  features?: Record<string, unknown>;
  recording_retention_days?: number;
  audio_retention_minutes?: number | null;
  created_at?: string;
}

const MIN_RETENTION_DAYS = 30;
const MAX_RETENTION_DAYS = 365;
const DEFAULT_RETENTION_DAYS = 365;
const MAX_AUDIO_RETENTION_MINUTES = 43200;
// Vaste keuzes uit het beleid; "Uit" (null) laat de audio de bewaartermijn volgen.
const AUDIO_RETENTION_PRESETS: Array<{ label: string; minutes: number }> = [
  { label: "1 min", minutes: 1 },
  { label: "24 u", minutes: 1440 },
  { label: "7 d", minutes: 10080 },
];

function parseEmails(value: string): string[] {
  return Array.from(
    new Set(
      value
        .split(/[\s,;]+/)
        .map((e) => e.trim().toLowerCase())
        .filter(Boolean),
    ),
  );
}

/**
 * Leeg → "uit" (null), geen fout. Niet-geheel/NaN/≤ 0 en > 43200 zijn fouten;
 * de admin moet expliciet "Uit" kiezen om audio te laten bewaren in plaats van
 * dat een 0 of negatief getal daar stilzwijgend naartoe afglijdt.
 */
function parseAudioMinutes(value: string): { value: number | null; error: string | null } {
  const trimmed = value.trim();
  if (trimmed === "") return { value: null, error: null };
  const parsed = Number(trimmed);
  if (!Number.isInteger(parsed) || parsed <= 0) {
    return {
      value: null,
      error: "Minimaal 1 minuut; gebruik Uit om audio te bewaren.",
    };
  }
  if (parsed > MAX_AUDIO_RETENTION_MINUTES) {
    return {
      value: null,
      error: `Maximaal ${MAX_AUDIO_RETENTION_MINUTES} minuten (30 dagen).`,
    };
  }
  return { value: parsed, error: null };
}

function RetentionConfirmModal({
  org,
  fromDays,
  toDays,
  impact,
  onCancel,
  onConfirm,
}: {
  org: Organization;
  fromDays: number;
  toDays: number;
  impact: RetentionImpact;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const [typed, setTyped] = useState("");
  const matches = typed.trim().toLowerCase() === org.name.trim().toLowerCase();
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50">
      <div className="w-full max-w-lg rounded-2xl border border-rose-200 bg-white p-6 shadow-xl">
        <h2 className="mb-2 text-lg font-bold text-rose-700">Retentie verkleinen</h2>
        <p className="mb-3 text-sm text-[var(--cs-navy)]">
          Bewaartermijn opnames van <strong>{fromDays}</strong> naar{" "}
          <strong>{toDays}</strong> dagen voor <strong>{org.name}</strong>.
        </p>
        <div className="mb-4 rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-900">
          <p>
            <strong>{impact.affected_count}</strong> opname(s) van{" "}
            <strong>{impact.affected_users}</strong> gebruiker(s) worden bij de
            volgende nachtelijke opruiming (06:17 Belgische tijd) <em>definitief verwijderd</em>.
          </p>
          {impact.oldest && impact.newest ? (
            <p className="mt-2 text-xs text-rose-700">
              Oudste: {new Date(impact.oldest).toLocaleDateString("nl-BE")} — Nieuwste:{" "}
              {new Date(impact.newest).toLocaleDateString("nl-BE")}
            </p>
          ) : null}
        </div>
        <label className="mb-1 block text-xs font-medium text-[var(--cs-navy)]">
          Typ <span className="font-mono">{org.name}</span> ter bevestiging:
        </label>
        <input
          type="text"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          autoFocus
          className="mb-4 w-full rounded-lg border border-[rgba(10,52,96,0.18)] px-3 py-1.5 text-sm"
        />
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg border border-[rgba(10,52,96,0.10)] px-4 py-1.5 text-sm text-[rgba(10,52,96,0.78)] hover:bg-[var(--cs-ivory)]"
          >
            Annuleren
          </button>
          <button
            type="button"
            disabled={!matches}
            onClick={onConfirm}
            className="rounded-lg bg-rose-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-rose-700 disabled:opacity-50"
          >
            Verkleinen en opruimen
          </button>
        </div>
      </div>
    </div>
  );
}


function OrganizationEditModal({
  org,
  isSuperuser,
  onClose,
  onSaved,
}: {
  org: Organization;
  isSuperuser: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(org.name);
  const [slug, setSlug] = useState(org.slug);
  const [emails, setEmails] = useState((org.admin_emails ?? []).join(", "));
  const [retention, setRetention] = useState<number>(
    org.recording_retention_days ?? DEFAULT_RETENTION_DAYS,
  );
  const [audioMinutesText, setAudioMinutesText] = useState<string>(
    org.audio_retention_minutes != null ? String(org.audio_retention_minutes) : "",
  );
  const audioMinutesParsed = parseAudioMinutes(audioMinutesText);
  const persistedAudioMinutes = useRef<number | null>(org.audio_retention_minutes ?? null);
  const [emailLogin, setEmailLogin] = useState(org.features?.widget_email_login === true);
  const persistedEmailLogin = useRef(org.features?.widget_email_login === true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [pendingConfirm, setPendingConfirm] = useState<{
    fromDays: number;
    toDays: number;
    impact: RetentionImpact;
  } | null>(null);

  const currentRetention = org.recording_retention_days ?? DEFAULT_RETENTION_DAYS;

  const persistOtherFields = useCallback(async (): Promise<boolean> => {
    const payload: {
      name?: string;
      slug?: string;
      admin_emails?: string[];
      audio_retention_minutes?: number;
      widget_email_login?: boolean;
    } = {};
    const trimmedName = name.trim();
    const normalizedSlug = normalizeSlug(slug);
    const newEmails = parseEmails(emails);
    const oldEmails = org.admin_emails ?? [];
    if (trimmedName !== org.name) payload.name = trimmedName;
    if (normalizedSlug !== org.slug) payload.slug = normalizedSlug;
    if (
      newEmails.length !== oldEmails.length ||
      newEmails.some((e, i) => e !== oldEmails[i])
    ) {
      payload.admin_emails = newEmails;
    }
    if (audioMinutesParsed.value !== persistedAudioMinutes.current) {
      // 0 betekent "uit" voor de API; null bestaat niet in de payload.
      payload.audio_retention_minutes = audioMinutesParsed.value ?? 0;
    }
    if (isSuperuser && emailLogin !== persistedEmailLogin.current) {
      payload.widget_email_login = emailLogin;
    }
    if (Object.keys(payload).length === 0) return true;
    await authFetchJson(`/api/admin/organizations/${org.id}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    });
    persistedAudioMinutes.current = audioMinutesParsed.value;
    persistedEmailLogin.current = emailLogin;
    return true;
  }, [name, slug, emails, audioMinutesParsed.value, isSuperuser, emailLogin, org]);

  const applyRetention = useCallback(async (days: number) => {
    await updateOrganizationRetention(org.id, days);
  }, [org.id]);

  const handleSave = async () => {
    setSaving(true);
    setError("");
    try {
      await persistOtherFields();

      if (retention !== currentRetention) {
        const clamped = Math.max(MIN_RETENTION_DAYS, Math.min(MAX_RETENTION_DAYS, retention));
        if (clamped < currentRetention) {
          // Destructive change — preview impact first.
          const impact = await getRetentionImpact(org.id, clamped);
          if (impact.affected_count > 0) {
            setPendingConfirm({ fromDays: currentRetention, toDays: clamped, impact });
            setSaving(false);
            return;
          }
        }
        await applyRetention(clamped);
      }
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Opslaan mislukt.");
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmShrink = async () => {
    if (!pendingConfirm) return;
    setSaving(true);
    setError("");
    try {
      await applyRetention(pendingConfirm.toDays);
      setPendingConfirm(null);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Opslaan mislukt.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="w-full max-w-lg rounded-2xl border border-[rgba(10,52,96,0.10)] bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-bold text-[var(--cs-navy)]">Organisatie bewerken</h2>
          <button type="button" onClick={onClose} className="text-[var(--cs-ink-subtle)] hover:text-[var(--cs-navy)]">
            ✕
          </button>
        </div>

        <div className="mb-3 rounded-lg bg-[var(--cs-ivory)] p-3 text-xs text-[rgba(10,52,96,0.78)]">
          <p>ID: {org.id.slice(0, 8)}…</p>
        </div>

        {error ? (
          <div className="mb-3 rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700">
            {error}
          </div>
        ) : null}

        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-[var(--cs-navy)]">Naam</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full rounded-lg border border-[rgba(10,52,96,0.18)] px-3 py-1.5 text-sm"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-[var(--cs-navy)]">Slug</label>
            <input
              type="text"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              className="w-full rounded-lg border border-[rgba(10,52,96,0.18)] px-3 py-1.5 font-mono text-sm"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-[var(--cs-navy)]">
              Admin e-mails (komma‑gescheiden)
            </label>
            <textarea
              rows={2}
              value={emails}
              onChange={(e) => setEmails(e.target.value)}
              className="w-full rounded-lg border border-[rgba(10,52,96,0.18)] px-3 py-1.5 text-sm"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-[var(--cs-navy)]">
              Bewaartermijn opnames (dagen)
            </label>
            <input
              type="number"
              min={MIN_RETENTION_DAYS}
              max={MAX_RETENTION_DAYS}
              value={retention}
              onChange={(e) => setRetention(Number(e.target.value) || 0)}
              className="w-32 rounded-lg border border-[rgba(10,52,96,0.18)] px-3 py-1.5 text-sm"
            />
            <p className="mt-1 text-xs text-[var(--cs-ink-muted)]">
              Tussen {MIN_RETENTION_DAYS} en {MAX_RETENTION_DAYS} dagen. Opnames ouder
              dan deze waarde worden dagelijks om 06:17 (Belgische tijd) definitief verwijderd.
              Verkleinen werkt onmiddellijk in op alle bestaande opnames.
            </p>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-[var(--cs-navy)]">
              Audio verwijderen na verwerking (minuten)
            </label>
            <div className="flex flex-wrap items-center gap-2">
              <input
                type="number"
                min={1}
                max={MAX_AUDIO_RETENTION_MINUTES}
                value={audioMinutesText}
                onChange={(e) => setAudioMinutesText(e.target.value)}
                className="w-32 rounded-lg border border-[rgba(10,52,96,0.18)] px-3 py-1.5 text-sm"
              />
              {AUDIO_RETENTION_PRESETS.map((preset) => (
                <button
                  key={preset.minutes}
                  type="button"
                  onClick={() => setAudioMinutesText(String(preset.minutes))}
                  className="rounded-lg border border-[rgba(10,52,96,0.10)] px-2 py-1 text-xs text-[rgba(10,52,96,0.78)] hover:bg-[var(--cs-ivory)]"
                >
                  {preset.label}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setAudioMinutesText("")}
                className="rounded-lg border border-[rgba(10,52,96,0.10)] px-2 py-1 text-xs text-[rgba(10,52,96,0.78)] hover:bg-[var(--cs-ivory)]"
              >
                Uit
              </button>
            </div>
            {audioMinutesParsed.error ? (
              <p className="mt-1 text-xs text-rose-600">{audioMinutesParsed.error}</p>
            ) : null}
            <p className="mt-1 text-xs text-[var(--cs-ink-muted)]">
              Leeg = audio volgt de bewaartermijn van opnames. Ingevuld = het audiobestand
              wordt zoveel minuten na verwerking verwijderd; daarna kan de opname niet meer
              beluisterd worden (app, telefoon, web). Transcripten en verslagen blijven
              bewaard.
            </p>
          </div>
          {isSuperuser ? (
            <div>
              <label className="flex items-center gap-2 text-xs font-medium text-[var(--cs-navy)]">
                <input
                  type="checkbox"
                  checked={emailLogin}
                  onChange={(e) => setEmailLogin(e.target.checked)}
                />
                Inloggen via e-maillink (widget en webapp)
              </label>
              <p className="mt-1 text-xs text-[var(--cs-ink-muted)]">
                Leden kunnen inloggen in de Windows-widget en de webapp met een eenmalige link
                (10 minuten geldig) die naar hun e-mailadres gaat, zonder passkey. Wie toegang
                heeft tot hun mailbox, kan dan ook inloggen in Carmen.
              </p>
            </div>
          ) : null}
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-[rgba(10,52,96,0.10)] px-4 py-1.5 text-sm text-[rgba(10,52,96,0.78)] hover:bg-[var(--cs-ivory)]"
          >
            Annuleren
          </button>
          <button
            type="button"
            onClick={() => void handleSave()}
            disabled={
              saving ||
              !name.trim() ||
              !normalizeSlug(slug) ||
              retention < MIN_RETENTION_DAYS ||
              retention > MAX_RETENTION_DAYS ||
              audioMinutesParsed.error !== null
            }
            className="rounded-lg bg-rose-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-rose-700 disabled:opacity-50"
          >
            {saving ? "Opslaan…" : "Opslaan"}
          </button>
        </div>
      </div>
      {pendingConfirm ? (
        <RetentionConfirmModal
          org={org}
          fromDays={pendingConfirm.fromDays}
          toDays={pendingConfirm.toDays}
          impact={pendingConfirm.impact}
          onCancel={() => setPendingConfirm(null)}
          onConfirm={() => void handleConfirmShrink()}
        />
      ) : null}
    </div>
  );
}

export function AdminOrganizationsTab() {
  const { accessRole } = useAuth();
  const isSuperuser = ROLE_LEVEL[accessRole] >= ROLE_LEVEL["superuser"];

  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [newName, setNewName] = useState("");
  const [newSlug, setNewSlug] = useState("");
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Organization | null>(null);
  const [busyId, setBusyId] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  useEffect(() => {
    setPage(0);
  }, [pageSize]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const resp = await authFetchJson<{ organizations: Organization[] }>(
        "/api/admin/organizations",
      );
      setOrgs(resp.organizations ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Laden mislukt.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const createOrg = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      const name = newName.trim();
      const slug = normalizeSlug(newSlug);
      if (!name || !slug) return;
      setCreating(true);
      setError("");
      try {
        await authFetchJson("/api/admin/organizations", {
          method: "POST",
          body: JSON.stringify({ name, slug }),
        });
        setNewName("");
        setNewSlug("");
        await load();
      } catch (err) {
        setError(err instanceof Error ? err.message : "Aanmaken mislukt.");
      } finally {
        setCreating(false);
      }
    },
    [newName, newSlug, load],
  );

  const deleteOrg = useCallback(
    async (org: Organization) => {
      if (
        !window.confirm(
          `Organisatie "${org.name}" permanent verwijderen? Dit kan niet ongedaan worden.`,
        )
      ) {
        return;
      }
      setBusyId(org.id);
      setError("");
      try {
        await authFetch(`/api/admin/organizations/${org.id}`, { method: "DELETE" });
        await load();
      } catch (err) {
        setError(err instanceof Error ? err.message : "Verwijderen mislukt.");
      } finally {
        setBusyId("");
      }
    },
    [load],
  );

  const copyOrgId = useCallback(async (id: string) => {
    await navigator.clipboard.writeText(id);
  }, []);

  const totalPages = Math.max(1, Math.ceil(orgs.length / pageSize));
  const currentPage = Math.min(page, totalPages - 1);
  const pagedOrgs = orgs.slice(currentPage * pageSize, (currentPage + 1) * pageSize);

  return (
    <div className="space-y-4">
      {isSuperuser ? (
        <form
          onSubmit={createOrg}
          className="flex flex-wrap items-end gap-2 rounded-2xl border border-[rgba(10,52,96,0.10)] bg-white p-4 shadow-sm"
        >
          <div className="min-w-0 flex-1">
            <label className="text-xs font-medium text-[var(--cs-navy)]" htmlFor="new-org-name">
              Naam
            </label>
            <input
              id="new-org-name"
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="bv. AZ Gent — Geneeskunde"
              className="mt-1 w-full rounded-lg border border-[rgba(10,52,96,0.18)] px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--cs-rose)]"
            />
          </div>
          <div className="min-w-0 flex-1">
            <label className="text-xs font-medium text-[var(--cs-navy)]" htmlFor="new-org-slug">
              Slug
            </label>
            <input
              id="new-org-slug"
              type="text"
              value={newSlug}
              onChange={(e) => setNewSlug(e.target.value)}
              placeholder="bv. az-gent-geneeskunde"
              className="mt-1 w-full rounded-lg border border-[rgba(10,52,96,0.18)] px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--cs-rose)]"
            />
          </div>
          <button
            type="submit"
            disabled={creating || !newName.trim() || !normalizeSlug(newSlug)}
            className="rounded-xl bg-rose-500 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-600 disabled:opacity-50"
          >
            {creating ? "Aanmaken…" : "Aanmaken"}
          </button>
        </form>
      ) : null}

      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      {loading ? (
        <p className="text-sm text-[var(--cs-ink-muted)]">Laden…</p>
      ) : orgs.length === 0 ? (
        <p className="text-sm text-[var(--cs-ink-muted)]">Nog geen organisaties.</p>
      ) : (
        <>
        <div className="overflow-x-auto rounded-2xl border border-[rgba(10,52,96,0.10)] glass-card">
          <table className="w-full text-sm">
            <thead className="bg-[var(--cs-ivory)]">
              <tr className="text-left text-[11px] font-semibold uppercase tracking-wider text-[var(--cs-ink-muted)]">
                <th className="px-4 py-2">Naam</th>
                <th className="px-4 py-2">Slug</th>
                <th className="px-4 py-2">Admin emails</th>
                <th className="px-4 py-2">ID</th>
                <th className="px-4 py-2">Acties</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[rgba(10,52,96,0.06)]">
              {pagedOrgs.map((org) => (
                <tr key={org.id} className="text-xs">
                  <td className="px-4 py-2 font-medium text-[var(--cs-navy)]">
                    {org.name}
                    {org.audio_retention_minutes ? (
                      <span className="ml-2 rounded-full bg-[rgba(10,52,96,0.08)] px-2 py-0.5 text-[10px] font-normal text-[var(--cs-ink-muted)]">
                        Audio: {formatAudioRetention(org.audio_retention_minutes)}
                      </span>
                    ) : null}
                  </td>
                  <td className="px-4 py-2 font-mono text-[rgba(10,52,96,0.78)]">{org.slug}</td>
                  <td className="px-4 py-2 text-[rgba(10,52,96,0.78)]">
                    {org.admin_emails?.join(", ") ?? "—"}
                  </td>
                  <td className="px-4 py-2">
                    <button
                      type="button"
                      onClick={() => void copyOrgId(org.id)}
                      className="font-mono text-[var(--cs-ink-muted)] hover:text-rose-600"
                      title="Kopieer ID"
                    >
                      {org.id.slice(0, 8)}…
                    </button>
                  </td>
                  <td className="px-4 py-2">
                    <div className="flex gap-1">
                      <button
                        type="button"
                        onClick={() => setEditing(org)}
                        disabled={busyId === org.id}
                        className="rounded border border-[rgba(10,52,96,0.10)] bg-white px-2 py-0.5 text-[10px] text-[var(--cs-navy)] hover:bg-[var(--cs-ivory)] disabled:opacity-50"
                      >
                        Bewerk
                      </button>
                      {isSuperuser ? (
                        <button
                          type="button"
                          onClick={() => void deleteOrg(org)}
                          disabled={busyId === org.id}
                          className="rounded border border-red-200 bg-white px-2 py-0.5 text-[10px] text-red-700 hover:bg-red-50 disabled:opacity-50"
                        >
                          Verwijder
                        </button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination
          total={orgs.length}
          page={currentPage}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
        </>
      )}

      {editing ? (
        <OrganizationEditModal
          org={editing}
          isSuperuser={isSuperuser}
          onClose={() => {
            setEditing(null);
            void load();
          }}
          onSaved={() => {
            setEditing(null);
            void load();
          }}
        />
      ) : null}
    </div>
  );
}
